/**
 * V5.8: 확인 모달 (범용)
 *
 * 기능:
 * - 삭제, 동기화, 시드 등 확인 필요한 액션에 사용
 * - 색상 테마 지원 (red, green, blue, purple)
 * - ESC 키로 닫기
 *
 * V5.8 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 */

import React, { useEffect } from 'react';
import { X, AlertTriangle, Info, Trash2, Download, Calendar } from 'lucide-react';
import { ModalOverlay } from '../../../../shared/components/ModalOverlay';
import styles from './holidaysStyles.module.css';

type ConfirmColor = 'red' | 'green' | 'blue' | 'purple';
type ConfirmIcon = 'alert' | 'info' | 'trash' | 'download' | 'calendar';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor?: ConfirmColor;
  icon?: ConfirmIcon;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function ConfirmModal({
  title,
  message,
  confirmLabel,
  confirmColor = 'blue',
  icon = 'info',
  onConfirm,
  onCancel,
  isLoading,
}: ConfirmModalProps) {
  // 아이콘 래퍼 클래스
  const getIconWrapperClass = (): string => {
    switch (confirmColor) {
      case 'red':
        return styles.confirmIconRed;
      case 'green':
        return styles.confirmIconGreen;
      case 'blue':
        return styles.confirmIconBlue;
      case 'purple':
        return styles.confirmIconPurple;
      default:
        return styles.confirmIconCyan;
    }
  };

  // 버튼 클래스
  const getPrimaryButtonClass = (): string => {
    switch (confirmColor) {
      case 'red':
        return styles.btnPrimaryRed;
      case 'green':
        return styles.btnPrimaryGreen;
      case 'blue':
        return styles.btnPrimaryBlue;
      case 'purple':
        return styles.btnPrimaryPurple;
      default:
        return styles.btnPrimary;
    }
  };

  // 아이콘 컴포넌트
  const Icon = () => {
    switch (icon) {
      case 'alert':
        return <AlertTriangle />;
      case 'trash':
        return <Trash2 />;
      case 'download':
        return <Download />;
      case 'calendar':
        return <Calendar />;
      default:
        return <Info />;
    }
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, isLoading]);

  return (
    <ModalOverlay
      className={styles.modalOverlay}
      data-testid="confirm-modal-overlay"
      onClose={isLoading ? undefined : onCancel}
    >
      <div
        className={styles.modalDialog}
        data-testid="confirm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className={styles.confirmHeader}>
          {/* 아이콘 */}
          <div className={getIconWrapperClass()}>
            <Icon />
          </div>

          {/* 제목/메시지 */}
          <div className={styles.confirmContent}>
            <h3 className={styles.confirmTitle}>{title}</h3>
            <p className={styles.confirmMessage}>{message}</p>
          </div>

          {/* 닫기 버튼 */}
          <button
            onClick={onCancel}
            disabled={isLoading}
            className={styles.confirmClose}
          >
            <X />
          </button>
        </div>

        {/* 버튼 */}
        <div className={styles.modalFooter}>
          <button
            onClick={onCancel}
            className={styles.btnSecondary}
            disabled={isLoading}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className={getPrimaryButtonClass()}
            disabled={isLoading}
          >
            {isLoading ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
